import type { ApiCloudTheme, ApiCloudThemeSettings } from '../api/types';
import type { ThemeKey } from '../types';

export type ThemeColorOverrides = Partial<Record<ThemeKey, Record<string, string>>>;

export const CLOUD_THEME_FORMAT = 'ios';

const IOS_LIGHT_BASE_THEMES = new Set<ApiCloudThemeSettings['baseTheme']>(['classic', 'day']);
const IOS_DARK_BASE_THEMES = new Set<ApiCloudThemeSettings['baseTheme']>(['night', 'tinted']);

const DEFAULT_ACCENT_COLOR = '#3390ec';
const DEFAULT_LIGHT_OWN_BACKGROUND = '#e1ffc7';
const DEFAULT_DARK_OWN_BACKGROUND = '#766ac8';
const IOS_DEFAULT_DARK_ACCENT = '#3e88f7';
const IOS_DEFAULT_DARK_MESSAGE_COLORS = ['#a256bf', '#9047ff', '#0771ff'];

type OwnPalette = {
  background: string;
  backgroundSelected: string;
  text: string;
  meta: string;
  links: string;
  replyHover: string;
  replyActive: string;
  reaction: string;
  reactionHover: string;
  voiceButton: string;
};

type HsvColor = {
  h: number;
  s: number;
  v: number;
};

type RgbColor = {
  r: number;
  g: number;
  b: number;
};

export function findCloudThemeById(themes: ApiCloudTheme[], id?: string) {
  if (!id) {
    return undefined;
  }

  return themes.find((theme) => theme.id === id);
}

export function findCloudTheme(themes: ApiCloudTheme[], {
  id,
  slug,
}: {
  id?: string;
  slug?: string;
}) {
  if (slug) {
    const bySlug = themes.find((theme) => theme.slug === slug);
    if (bySlug) {
      return bySlug;
    }
  }

  return findCloudThemeById(themes, id);
}

export function resolveCloudThemeSettings(theme: ApiCloudTheme, key: ThemeKey): ApiCloudThemeSettings | undefined {
  const { settings } = theme;

  if (!settings.length) {
    return undefined;
  }

  const targetBaseThemes = key === 'dark' ? IOS_DARK_BASE_THEMES : IOS_LIGHT_BASE_THEMES;
  const exactMatch = settings.find((setting) => targetBaseThemes.has(normalizeBaseTheme(setting.baseTheme)));
  if (exactMatch) {
    return exactMatch;
  }

  return settings[0];
}

export function resolveCloudThemeColorOverrides(
  themes: ApiCloudTheme[],
  cloudThemeId?: string,
): ThemeColorOverrides | undefined {
  const theme = findCloudThemeById(themes, cloudThemeId);
  if (!theme) {
    return undefined;
  }

  const lightSettings = resolveCloudThemeSettings(theme, 'light');
  const darkSettings = resolveCloudThemeSettings(theme, 'dark');

  const overrides: ThemeColorOverrides = {};

  if (lightSettings) {
    overrides.light = buildThemeOverridesFromSettings(lightSettings, 'light');
  }

  if (darkSettings) {
    overrides.dark = buildThemeOverridesFromSettings(darkSettings, 'dark');
  }

  return Object.keys(overrides).length ? overrides : undefined;
}

function buildThemeOverridesFromSettings(settings: ApiCloudThemeSettings, key: ThemeKey): Record<string, string> {
  const baseTheme = normalizeBaseTheme(settings.baseTheme);
  const isDark = IOS_DARK_BASE_THEMES.has(baseTheme) || key === 'dark';
  const accentSource = normalizeHexColor(settings.accentColor || DEFAULT_ACCENT_COLOR);
  const accent = normalizeAccentForBaseTheme(accentSource, baseTheme);
  const bubbleColors = resolveBubbleColors(settings, accent);
  const outboxAccent = resolveEffectiveOutboxAccent(settings, accent);
  const own = buildOwnPalette(baseTheme, bubbleColors, outboxAccent);

  const overrides: Record<string, string> = {
    '--color-primary': accent,
    '--color-primary-shade': darken(accent, 0.15),
    '--color-primary-tint': withAlpha(accent, 0.1),
    '--color-primary-opacity': withAlpha(accent, 0.12),
    '--color-primary-opacity-hover': withAlpha(accent, 0.25),
    '--color-links': accent,
    '--color-code': accent,
    '--color-chat-active': accent,
    '--color-chat-active-greyed': blend(accent, '#ffffff', isDark ? 0.24 : 0.18),

    '--color-background-own': own.background,
    '--color-background-own-apple': own.background,
    '--color-background-own-selected': own.backgroundSelected,
    '--color-reply-own-hover': own.replyHover,
    '--color-reply-own-hover-apple': own.replyHover,
    '--color-reply-own-active': own.replyActive,
    '--color-reply-own-active-apple': own.replyActive,

    '--color-accent-own': own.text,
    '--color-message-meta-own': own.meta,
    '--color-own-links': own.links,
    '--color-code-own': own.text,
    '--color-message-reaction-own': own.reaction,
    '--color-message-reaction-hover-own': own.reactionHover,
    '--color-voice-transcribe-button-own': own.voiceButton,
    '--color-text-meta-colored': accent,
  };

  if (baseTheme === 'tinted') {
    Object.assign(overrides, buildTintedBackgroundOverrides(accent));
  }

  return overrides;
}

function resolveBubbleColors(settings: ApiCloudThemeSettings, accent: string) {
  const baseTheme = normalizeBaseTheme(settings.baseTheme);
  const messageColors = (settings.messageColors || []).map(normalizeHexColor).slice(0, 4);
  if (messageColors.length) {
    return messageColors;
  }

  switch (baseTheme) {
    case 'day':
      return [withMultiplied(accent, 0.966, 0.61, 0.98), accent];
    case 'classic': {
      const { h, s, v } = hexToHsv(accent);
      const saturation = (s > 0 && v > 0) ? 0.14 : 0;
      const brightness = clamp01(0.79 + v * 0.21);
      return [hsvToHex({ h, s: saturation, v: brightness })];
    }
    case 'night':
      if (isSameColor(accent, '#ffffff')) {
        return ['#313131', '#313131'];
      }
      if (isSameColor(accent, IOS_DEFAULT_DARK_ACCENT)) {
        return IOS_DEFAULT_DARK_MESSAGE_COLORS;
      }
      return [withMultiplied(accent, 0.966, 0.61, 0.98), accent];
    case 'tinted': {
      const bottomColor = withMultiplied(accent, 1.019, 0.731, 0.59);
      const topColor = withMultiplied(bottomColor, 0.966, 0.61, 0.98);
      return [topColor, bottomColor];
    }
    default:
      return [accent];
  }
}

function resolveEffectiveOutboxAccent(settings: ApiCloudThemeSettings, accent: string) {
  if (settings.outboxAccentColor) {
    return normalizeHexColor(settings.outboxAccentColor);
  }

  if (normalizeBaseTheme(settings.baseTheme) !== 'classic' || settings.messageColors?.length) {
    return undefined;
  }

  if (getPerceivedLightness(accent) > 0.705) {
    return withSaturationAndBrightnessCap(accent, 1.1, 0.6);
  }

  return accent;
}

function buildOwnPalette(
  baseTheme: ApiCloudThemeSettings['baseTheme'],
  bubbleColors: string[],
  outboxAccentColor?: string,
): OwnPalette {
  const normalizedBaseTheme = normalizeBaseTheme(baseTheme);
  const isDarkBaseTheme = IOS_DARK_BASE_THEMES.has(normalizedBaseTheme);
  const fallbackBackground = isDarkBaseTheme ? DEFAULT_DARK_OWN_BACKGROUND : DEFAULT_LIGHT_OWN_BACKGROUND;
  const background = bubbleColors[0] || fallbackBackground;
  const middleColor = bubbleColors[Math.floor((bubbleColors.length - 1) / 2)] || background;
  const backgroundSelected = resolveOwnSelectedBackground(normalizedBaseTheme, middleColor);
  const averageColor = averageColors(bubbleColors.length ? bubbleColors : [background]);

  if (isDarkBaseTheme) {
    const lightnessThreshold = normalizedBaseTheme === 'tinted' ? 0.7 : 0.735;
    const text = getPerceivedLightness(averageColor) > lightnessThreshold ? '#000000' : '#ffffff';
    const replyHover = blend(background, backgroundSelected, 0.42);
    const replyActive = backgroundSelected;

    return {
      background,
      backgroundSelected,
      text,
      meta: withAlpha(text, 0.5),
      links: text,
      replyHover,
      replyActive,
      reaction: blend(backgroundSelected, '#ffffff', 0.08),
      reactionHover: blend(backgroundSelected, '#ffffff', 0.14),
      voiceButton: blend(background, '#ffffff', 0.12),
    };
  }

  const isBright = getPerceivedLightness(averageColor) > 0.705;
  const replyHover = blend(background, backgroundSelected, 0.42);
  const replyActive = backgroundSelected;

  if (!isBright) {
    return {
      background,
      backgroundSelected,
      text: '#ffffff',
      meta: withAlpha('#ffffff', 0.65),
      links: '#ffffff',
      replyHover,
      replyActive,
      reaction: blend(backgroundSelected, '#ffffff', 0.14),
      reactionHover: blend(backgroundSelected, '#000000', 0.05),
      voiceButton: blend(background, '#ffffff', 0.22),
    };
  }

  if (outboxAccentColor) {
    return {
      background,
      backgroundSelected,
      text: outboxAccentColor,
      meta: withAlpha(outboxAccentColor, 0.8),
      links: outboxAccentColor,
      replyHover,
      replyActive,
      reaction: blend(backgroundSelected, '#ffffff', 0.15),
      reactionHover: blend(backgroundSelected, '#000000', 0.05),
      voiceButton: blend(outboxAccentColor, '#ffffff', 0.62),
    };
  }

  const hueFactor = 0.75;
  const saturationFactor = 1.1;
  const ownText = withMultiplied(averageColor, 1.302 * hueFactor, 4.554 * saturationFactor, 0.655);
  const metaBase = withMultiplied(background, 1.344 * hueFactor, 4.554 * saturationFactor, 0.549);
  const outgoingControl = withMultiplied(averageColor, 1.283 * hueFactor, 3.176, 0.765);

  return {
    background,
    backgroundSelected,
    text: ownText,
    meta: withAlpha(metaBase, 0.8),
    links: '#004bad',
    replyHover,
    replyActive,
    reaction: blend(backgroundSelected, '#ffffff', 0.15),
    reactionHover: blend(backgroundSelected, '#000000', 0.05),
    voiceButton: blend(outgoingControl, '#ffffff', 0.4),
  };
}

function resolveOwnSelectedBackground(baseTheme: ApiCloudThemeSettings['baseTheme'], middleColor: string) {
  const normalizedBaseTheme = normalizeBaseTheme(baseTheme);

  if (normalizedBaseTheme === 'night') {
    if (hexToHsv(middleColor).v > 0.98) {
      return withMultiplied(middleColor, 1.0, 0.87, 1.0);
    }
    return withMultiplied(middleColor, 1.0, 1.1, 1.121);
  }

  if (normalizedBaseTheme === 'tinted') {
    return withMultiplied(middleColor, 1.019, 0.609, 0.63);
  }

  const middleLightness = getPerceivedLightness(middleColor);
  if (middleLightness < 0.85) {
    return withMultiplied(multiplyColors(middleColor, middleColor), 1.0, 0.6, 1.0);
  }

  if (middleLightness > 0.97) {
    return withMultiplied(middleColor, 1.0, 2.359, 0.99);
  }

  return withMultiplied(middleColor, 1.0, 1.589, 0.99);
}

function normalizeAccentForBaseTheme(accent: string, baseTheme: ApiCloudThemeSettings['baseTheme']) {
  const normalizedBaseTheme = normalizeBaseTheme(baseTheme);

  if ((normalizedBaseTheme === 'day' || normalizedBaseTheme === 'classic') && getPerceivedLightness(accent) > 0.705) {
    return withSaturationAndBrightnessCap(accent, 1.1, 0.6);
  }

  if (normalizedBaseTheme === 'night' && !isSameColor(accent, '#ffffff') && getPerceivedLightness(accent) <= 0.735) {
    return withBrightnessFloor(accent, 0.55);
  }

  if (normalizedBaseTheme === 'tinted') {
    return withBrightnessFloor(accent, 0.18);
  }

  return accent;
}

function normalizeBaseTheme(baseTheme: ApiCloudThemeSettings['baseTheme']): ApiCloudThemeSettings['baseTheme'] {
  // iOS maps `baseThemeArctic` to the same branch as `day`.
  return baseTheme === 'arctic' ? 'day' : baseTheme;
}

function buildTintedBackgroundOverrides(accent: string): Record<string, string> {
  const mainBackground = withMultiplied(accent, 1.024, 0.585, 0.25);
  const selectedBackground = withMultiplied(accent, 1.03, 0.585, 0.12);
  const additionalBackground = withMultiplied(accent, 1.024, 0.573, 0.18);
  const separatorColor = withMultiplied(accent, 1.033, 0.426, 0.34);
  const mainForeground = withMultiplied(accent, 0.99, 0.256, 0.62);
  const secondaryText = withMultiplied(accent, 0.956, 0.17, 1.0);
  const inputColor = withMultiplied(accent, 1.029, 0.609, 0.19);

  return {
    '--color-background': mainBackground,
    '--color-background-secondary': additionalBackground,
    '--color-background-secondary-accent': inputColor,
    '--color-background-selected': selectedBackground,
    '--color-chat-hover': selectedBackground,
    '--color-item-hover': selectedBackground,
    '--color-item-active': blend(selectedBackground, '#000000', 0.2),

    '--color-text-secondary': withAlpha(secondaryText, 0.5),
    '--color-icon-secondary': withAlpha(secondaryText, 0.5),
    '--color-text-secondary-apple': withAlpha(secondaryText, 0.5),
    '--color-borders': separatorColor,
    '--color-borders-input': separatorColor,
    '--color-dividers': separatorColor,
    '--color-dividers-android': additionalBackground,
    '--color-gray': withAlpha(secondaryText, 0.5),
    '--color-list-icon': withAlpha(secondaryText, 0.5),

    '--color-chat-active-greyed': mainForeground,
    '--color-reply-hover': selectedBackground,
    '--color-reply-active': blend(selectedBackground, '#000000', 0.15),
  };
}

function normalizeHexColor(color: string) {
  const sanitized = ((color || '').startsWith('#') ? color.slice(1) : (color || ''))
    .replace(/[^a-f0-9]/gi, '')
    .toLowerCase();

  if (sanitized.length === 3) {
    const [r, g, b] = sanitized;
    return `#${r}${r}${g}${g}${b}${b}`;
  }

  if (sanitized.length >= 6) {
    return `#${sanitized.slice(0, 6)}`;
  }

  if (!sanitized.length) {
    return '#000000';
  }

  return `#${sanitized.padStart(6, '0').slice(-6)}`;
}

function getPerceivedLightness(color: string) {
  const { r, g, b } = hexToRgb(color);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function isSameColor(first: string, second: string) {
  return normalizeHexColor(first) === normalizeHexColor(second);
}

function averageColors(colors: string[]) {
  const palette = colors.length ? colors : [DEFAULT_ACCENT_COLOR];
  const aggregate = palette.reduce((acc, color) => {
    const { r, g, b } = hexToRgb(color);
    return {
      r: acc.r + r,
      g: acc.g + g,
      b: acc.b + b,
    };
  }, { r: 0, g: 0, b: 0 });

  return rgbToHex({
    r: aggregate.r / palette.length,
    g: aggregate.g / palette.length,
    b: aggregate.b / palette.length,
  });
}

function withMultiplied(color: string, hue: number, saturation: number, brightness: number) {
  const hsv = hexToHsv(color);
  return hsvToHex({
    h: clamp01(hsv.h * hue),
    s: clamp01(hsv.s * saturation),
    v: clamp01(hsv.v * brightness),
  });
}

function withSaturationAndBrightnessCap(color: string, saturationFactor: number, brightnessCap: number) {
  const hsv = hexToHsv(color);
  return hsvToHex({
    h: hsv.h,
    s: clamp01(hsv.s * saturationFactor),
    v: Math.min(hsv.v, brightnessCap),
  });
}

function withBrightnessFloor(color: string, brightnessFloor: number) {
  const hsv = hexToHsv(color);
  return hsvToHex({
    h: hsv.h,
    s: hsv.s,
    v: Math.max(hsv.v, brightnessFloor),
  });
}

function multiplyColors(first: string, second: string) {
  const c1 = hexToRgb(first);
  const c2 = hexToRgb(second);
  return rgbToHex({
    r: (c1.r / 255) * (c2.r / 255) * 255,
    g: (c1.g / 255) * (c2.g / 255) * 255,
    b: (c1.b / 255) * (c2.b / 255) * 255,
  });
}

function hexToHsv(color: string): HsvColor {
  const { r, g, b } = hexToRgb(color);

  const nr = r / 255;
  const ng = g / 255;
  const nb = b / 255;
  const max = Math.max(nr, ng, nb);
  const min = Math.min(nr, ng, nb);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === nr) {
      h = ((ng - nb) / delta + (ng < nb ? 6 : 0)) / 6;
    } else if (max === ng) {
      h = ((nb - nr) / delta + 2) / 6;
    } else {
      h = ((nr - ng) / delta + 4) / 6;
    }
  }

  const s = max === 0 ? 0 : delta / max;
  return { h, s, v: max };
}

function hsvToHex(hsv: HsvColor) {
  const h = clamp01(hsv.h);
  const s = clamp01(hsv.s);
  const v = clamp01(hsv.v);

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  const candidates: RgbColor[] = [
    { r: v * 255, g: t * 255, b: p * 255 },
    { r: q * 255, g: v * 255, b: p * 255 },
    { r: p * 255, g: v * 255, b: t * 255 },
    { r: p * 255, g: q * 255, b: v * 255 },
    { r: t * 255, g: p * 255, b: v * 255 },
    { r: v * 255, g: p * 255, b: q * 255 },
  ];

  return rgbToHex(candidates[i % 6]);
}

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

function darken(color: string, amount: number) {
  return blend(color, '#000000', amount);
}

function blend(baseColor: string, overlayColor: string, overlayRatio: number) {
  const ratio = Math.max(0, Math.min(1, overlayRatio));
  const base = hexToRgb(baseColor);
  const overlay = hexToRgb(overlayColor);

  return rgbToHex({
    r: Math.round(base.r * (1 - ratio) + overlay.r * ratio),
    g: Math.round(base.g * (1 - ratio) + overlay.g * ratio),
    b: Math.round(base.b * (1 - ratio) + overlay.b * ratio),
  });
}

function withAlpha(color: string, opacity: number) {
  const alpha = Math.round(Math.max(0, Math.min(1, opacity)) * 255);
  return `${normalizeHexColor(color)}${alpha.toString(16).padStart(2, '0')}`;
}

function hexToRgb(hexColor: string) {
  const hex = normalizeHexColor(hexColor || '#000000').slice(1);
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }) {
  const toHex = (channel: number) => Math.round(channel).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
